import { isValidElement, ReactNode, useCallback, useContext } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { mobileWidth } from '@/constants';
import { LayoutContext } from '@/contexts/layoutContext';

interface HeaderInterface {
  /** Plain string titles render as bold `h5`. Pass a React element for custom title UI (e.g. inline tag selector). */
  HeadName: ReactNode;
  HeadMenu?: ReactNode;
}

const Header: React.FC<HeaderInterface> = ({ HeadName, HeadMenu }) => {
  const isMobileMatch = useMediaQuery(mobileWidth);
  const { layout, setLayout } = useContext(LayoutContext);

  const handleMenuOpen = useCallback(() => {
    setLayout({ ...layout, isMenuOpen: true });
  }, [layout, setLayout]);

  return (
    <>
      <Paper
        elevation={0}
        square={!isMobileMatch}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          fontWeight: 'bold',
          borderTopRightRadius: isMobileMatch ? 16 : null,
          borderTopLeftRadius: isMobileMatch ? 16 : null,
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottom: '1px solid #e0e0e0',
          minHeight: 72,
          boxSizing: 'border-box',
          py: 1,
          pr: isMobileMatch ? 2 : 3,
          pl: isMobileMatch ? 1 : 3,
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          sx={{ minWidth: 0, flex: '1 1 auto', maxWidth: '100%' }}
        >
          {isMobileMatch && (
            <Button
              onClick={handleMenuOpen}
              sx={{ minWidth: 0, p: 1 }}
            >
              <MenuRoundedIcon />
            </Button>
          )}
          {isValidElement(HeadName) ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              {HeadName}
            </Box>
          ) : (
            <Typography
              sx={{ fontWeight: 'bold' }}
              variant='h5'
            >
              {HeadName}
            </Typography>
          )}
        </Stack>
        {HeadMenu != null && HeadMenu !== false ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginLeft: 'auto',
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            {HeadMenu}
          </Box>
        ) : null}
      </Paper>
    </>
  );
};

export default Header;
